import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// import DynamicTable from "../../../../components/Dynamic-table";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";
import { FilterListOutlined } from "@mui/icons-material";
import DynamicTable from "../../../../components/lab-dynamic-table";
import {
  getDefaultRecordDeleteTooltip,
  getDefaultRecordEditTooltip,
  isDefaultLabRecord,
} from "../../utils/defaultRecordActions";

const TestTab: React.FC = () => {
  const navigate = useNavigate();

  const [tableData, setTableData] = useState<any[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [searchFilter, setSearchFilter] = useState<
    "testName" | "specimen" | "displayName" | "labDepartmentId"
  >("testName");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [departments, setDepartments] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);
  // fetch tests

  const fetchTests = async () => {
    try {
      setLoading(true);
      const params: Record<string, any> = {
        pageNo: page + 1,
        count: rowsPerPage,
        search: debouncedSearch,
      };

      if (debouncedSearch?.trim()) {
        params.searchBy = searchFilter;
      }
      if (selectedDepartment) {
        params.labDepartmentId = selectedDepartment;
      }
      const url = `${process.env.REACT_APP_LAB_URL}/tests/getAllTests`;
      const res = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      const data = res.data?.data ?? [];
      setTableData(Array.isArray(data) ? data : []);
      setTotal(res.data?.totalCount || 0);
      if (departments.length === 0) {
        const deptMap = new Map<string, string>();
        data.forEach((item: any) => {
          if (
            item.labDepartment?._id &&
            item.labDepartment?.labDepartmentName
          ) {
            deptMap.set(
              item.labDepartment._id,
              item.labDepartment.labDepartmentName
            );
          }
        });
        setDepartments(Array.from(deptMap, ([id, name]) => ({ id, name })));
      }
    } catch (err: any) {
      console.error("fetchTests error", err);
      toast.error(err?.message || "Error fetching tests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTests();
  }, [page, rowsPerPage, debouncedSearch, selectedDepartment]);

  // delete flow
  const handleDeleteClick = (id: string) => {
    setSelectedItemId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItemId) {
      setIsDeleteModalOpen(false);
      return;
    }
    try {
      const url = `${process.env.REACT_APP_LAB_URL}/tests/deleteTest/${selectedItemId}`;
      const res = await axios.delete(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      toast.success(res.data?.message || "Deleted successfully");
      // refresh list
      await fetchTests();
    } catch (err: any) {
      console.error("delete error", err);
      toast.error(err?.message || "Error deleting test");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedItemId("");
    }
  };

  // Action handlers for DynamicTable actionButtons which receive rowIndex
  const actionButtons = [
    {
      icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
      onClick: (rowIndex: number) => {
        const id = tableData[rowIndex]?._id;
        const isPcr =
          tableData[rowIndex].testResultType.includes("PCR's special tests") ||
          tableData[rowIndex].testResultType.includes("Culture") ||
          tableData[rowIndex].testResultType.includes("Culture Urine");
        if (!isPcr) {
          localStorage.setItem("checkType", "false");
        }
        console.log(isPcr, "");
        if (id) {
          navigate(`/test-tab/create-test/${id}?total=${total + 2}`);
        } else {
          toast.error("Unable to find test id");
        }
      },
      disabled: (rowIndex: number) => isDefaultLabRecord(tableData[rowIndex]),
      tooltip: (rowIndex: number) =>
        getDefaultRecordEditTooltip(tableData[rowIndex]),
    },
    {
      icon: <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />,
      onClick: (rowIndex: number) => {
        const id = tableData[rowIndex]?._id;
        if (id) handleDeleteClick(id);
        else toast.error("Unable to find test id");
      },
      disabled: (rowIndex: number) => isDefaultLabRecord(tableData[rowIndex]),
      tooltip: (rowIndex: number) =>
        getDefaultRecordDeleteTooltip(tableData[rowIndex]),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          p: 0.5,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
            mb: { xs: 1, sm: 0 },
          }}
        >
          Test
        </Typography>

        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1}
          width={{ xs: "100%", sm: "auto" }}
        >
          <TextField
            select
            label="Search By Department"
            className="custom-input"
            value={selectedDepartment || ""}
            onChange={e => {
              setSelectedDepartment(e.target.value);
            }}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 150 },
              width: { xs: "100%", sm: "auto" },
            }}
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: "0.85rem", mt: 0.4 },
            }}
            SelectProps={{
              displayEmpty: true,
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "14px",
                      padding: "6px 8px",
                      minHeight: "unset",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            {departments.map(dept => (
              <MenuItem key={dept.id} value={dept.id} className="custom-input">
                {dept.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Group: search filter + value */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={1}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 400,
                  mr: { xs: 0, sm: 1 },
                  mb: { xs: 0.5, sm: 0 },
                }}
              >
                Search By:
              </Typography>

              <TextField
                select
                label="Search By"
                className="custom-input"
                value={searchFilter}
                onChange={e =>
                  setSearchFilter(
                    e.target.value as "testName" | "specimen" | "displayName"
                  )
                }
                size="small"
                sx={{ width: { xs: "100%", sm: 140 } }}
                InputLabelProps={{
                  sx: { fontSize: "0.8rem", mt: 0.5 },
                }}
              >
                <MenuItem value="testName">Test Name</MenuItem>
                <MenuItem value="specimen">Specimen</MenuItem>
                <MenuItem value="displayName">Display Name</MenuItem>
              </TextField>
            </Box>

            {/* Search Value with Label */}
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              width={{ xs: "100%", sm: "auto" }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 400, mr: { xs: 0, sm: 1 } }}
              >
                Search Value:
              </Typography>
              <TextField
                placeholder="Enter search value..."
                variant="outlined"
                size="small"
                className="custom-input"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                sx={{ width: { xs: "100%", sm: 180 } }}
                InputProps={{
                  endAdornment: (
                    <IconButton>
                      <SearchIcon sx={{ color: "#055FFC" }} />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          </Box>

          {/* Add New Test button: full width on mobile */}
          <Box display="flex" alignItems="center">
            <Button
              variant="contained"
              onClick={() =>
                navigate(`/test-tab/create-test?total=${total + 1}`)
              }
              sx={{
                fontSize: ".78rem",
                width: { xs: "100%", sm: "auto" },
                whiteSpace: "nowrap",
              }}
            >
              Add New Test
            </Button>
          </Box>
        </Box>
      </Box>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <DynamicTable
            headers={[
              "Test Code",
              "Department",
              "Test Name",
              "Display Name",
              "Result Type",
              "Order By",
              "Charges",
              "Specimen",
              "Actions",
            ]}
            data={tableData?.map((item: any, index: number) => [
              item?.testCode || "",
              item?.labDepartment?.labDepartmentName ??
                item?.labDepartmentId ??
                "",
              item?.testName ?? "",
              item?.displayName ?? "",
              item?.testResultType ?? "",
              item?.orderBy ?? "",
              item?.testCharges ?? "",
              item?.specimen ?? "",
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={(e: any, newPage: number) => setPage(newPage)}
            handleChangeRowsPerPage={(e: any) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            totalRows={total}
            actionButtons={actionButtons}
            height={"60vh"}
          />
        </Grid>
      </Grid>
      {/* Delete confirmation modal */}
      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default TestTab;
